/* eslint-disable react/no-array-index-key */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useField, useForm } from 'react-final-form';
import { Tooltip } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';

import { useLocale } from '../../locale';

/**
 * @param {string} str
 */
const pascalCase = (str) => `${str[0].toUpperCase()}${str.substring(1)}`;

/**
 * @param {moment.Moment} date
 * @returns {Array<Array<null|{value: number, date: moment.Moment}>>}
 */
const getDays = (date) => {
  const startOfMonth = date.clone().startOf('month');
  const initialMonth = startOfMonth.month();

  let indexArray = 0;
  const arr = [[], [], [], [], [], []];

  for (let i = 0; i < startOfMonth.day(); i++) {
    arr[indexArray].push(null);
  }

  for (let dayNum = 1; dayNum <= 50; dayNum++) {
    const temp = startOfMonth.clone().date(dayNum);

    if (!arr[indexArray]) {
      break;
    }
    if (temp.month() === initialMonth) {
      arr[indexArray].push({ value: dayNum, date: temp });
    } else {
      arr[indexArray].push(null);
    }
    if (arr[indexArray].length >= 7) {
      indexArray++;
    }
  }
  return arr;
};

/**
 * @param {moment.Moment} date
 * @returns {string}
 */
const convertDateToYYYYMMDD = (date) => date.format('YYYY-MM-DD');

const useTableHeadRows = () => {
  const [locale] = useLocale();

  const daysOfTheWeek = useMemo(() => {
    const initDate = moment().locale(locale);
    initDate.subtract(initDate.day(), 'days');

    const out = [];
    for (let i = 0; i < 7; i++) {
      const shortDay = initDate.format('ddd');
      out.push(pascalCase(shortDay));
      initDate.add(1, 'day');
    }
    return out;
  }, [locale]);

  const { toggleDayOfTheWeek } = useContext(YearViewDatePickerInputContext);

  const daysWithMonth = [{ large: true }, ...daysOfTheWeek.map((d) => ({ content: d }))];

  return { daysWithMonth, toggleDayOfTheWeek };
};

/**
 * @param {object} props
 * @param {moment.Moment} props.date
 */
const TableHeadRows = ({ date = moment() }) => {
  const { daysWithMonth, toggleDayOfTheWeek } = useTableHeadRows();

  return (
    <tr>
      {daysWithMonth.map((r, i) => <th
        key={i}
        className="year-view-date-picker-calendar-date-col"
        style={{
          width: r.large ? undefined : '2rem',
        }}
      >
        <div onClick={() => toggleDayOfTheWeek(date, i - 1)}>{r.content}</div>
      </th>
      )}
    </tr>
  );
};

/**
 * @param {{ value: number, date: moment.Moment }} value
 */
const useRowSelectedComponent = (value) => {
  const { toggleDate, listDateSelected, validateDate, setValidating } = useContext(YearViewDatePickerInputContext);
  const [stateDate, setStateDate] = useState();
  const [stateError, setStateError] = useState();

  const isSelected = useMemo(() => {
    return listDateSelected.some((e) => e.isSame(value.date, 'day'));
  }, [listDateSelected, value]);

  const click = useCallback(() => {
    toggleDate(value.date);
  }, [toggleDate, value]);

  useEffect(() => {
    (async () => {
      if (!isSelected) {
        setStateDate();
        setStateError();
        return;
      }
      setValidating(true);
      setStateDate('loading');
      setStateError();

      const { errors } = await validateDate(value.date) || {};
      if (errors) {
        setStateDate('error');
        setStateError(errors);
      } else {
        setStateDate('success');
      }
      setValidating(false);
    })();
  }, [isSelected, validateDate, value, setValidating]);

  return { isSelected, click, stateDate, stateError };
};

const RowSelectedComponent = ({ className, value }) => {
  const { isSelected, click, stateDate, stateError } = useRowSelectedComponent(value);
  const translate = useTranslate();

  let contentTooltip;
  if (stateDate === 'loading') {
    contentTooltip = (
      <Tooltip
        title={translate('ra.message.validating')}
        placement="top"
      >
        <div>{value.value}</div>
      </Tooltip>
    );
  } else if (stateDate === 'error') {
    const numOfErrors = stateError?.length;
    contentTooltip = (
      <Tooltip
        title={translate('resources.arenaslots.validations.slot_conflicts', numOfErrors)} placement="top">
        <div>{value.value}</div>
      </Tooltip>
    );
  } else {
    contentTooltip = value.value;
  }

  const onKeyUp = useCallback((e) => {
    if (e.key === 'Enter') {
      click();
    }
  }, [click]);

  return <td className={className} onClick={click} onKeyUp={onKeyUp} tabIndex="0">
    <div className={isSelected ? `selected-${stateDate}` : ''}>
      {contentTooltip}
    </div>
  </td>
};

const useTableBodyRows = (date) => {
  const [locale] = useLocale();

  const rows = useMemo(() => {
    const m1 = date.clone();
    const m2 = date.clone().add(1, 'month');
    const m3 = date.clone().add(2, 'month');
    const m4 = date.clone().add(3, 'month');

    const pushOnFirst = (arr, element) => {
      const out = arr.reverse();
      out.push(element);
      return out.reverse();
    };

    const t1 = getDays(m1);
    t1[0] = pushOnFirst(
      t1[0],
      pascalCase(m1.locale(locale).format('MMMM YYYY'))
    );

    const t2 = getDays(m2);
    t2[0] = pushOnFirst(
      t2[0],
      pascalCase(m2.locale(locale).format('MMMM YYYY'))
    );

    const t3 = getDays(m3);
    t3[0] = pushOnFirst(
      t3[0],
      pascalCase(m3.locale(locale).format('MMMM YYYY'))
    );

    const t4 = getDays(m4);
    t4[0] = pushOnFirst(
      t4[0],
      pascalCase(m4.locale(locale).format('MMMM YYYY'))
    );

    return [...t1, ...t2, ...t3, ...t4];
  }, [date, locale]);

  return { rows };
};

const TableBodyRows = ({ date = moment(), firstHighlight = false }) => {
  const { rows } = useTableBodyRows(date);

  let t = 0;

  return <>
    {rows.map((r, i) => {
      if (i % 6 === 0) {
        t++;
      }
      return <tr key={i}>
        {r.map((c, j) => {
          const background = t % 2 === Number(firstHighlight) ? '#8080801a' : undefined;

          if (typeof c === 'string') {
            const [month, year] = c.split(' ');
            return (
              <td
                rowSpan={6}
                className="year-view-date-picker-calendar-date-month"
                style={{
                  background: background ? undefined : '#8080804d',
                }}
                key={j}
              >
                <div>
                  <div>{month}</div>
                  <div>{year}</div>
                </div>
              </td>
            );
          }

          const className = `year-view-date-picker-calendar-date${
            background ? '-dark' : ''
          }`;
          if (c && typeof c.value === 'number' && c.value <= 31) {
            return <RowSelectedComponent className={className} key={j} value={c} />;
          }

          return <td className={className} style={{ cursor: 'auto' }} key={j} />;
        })}
      </tr>
    })}
  </>
};

const isSameDateArray = (a, b) => {
  const bStrings = b.map((m) => convertDateToYYYYMMDD(m));
  return JSON.stringify(a) === JSON.stringify(bStrings);
};

/**
 * @type {React.Context<{
 *  listDateSelected: moment.Moment[],
 *  toggleDate: (date: moment.Moment) => void,
 *  toggleDayOfTheWeek: (initDate: moment.Moment, dayOfTheWeek: number) => void,
 *  validateDate: (date: moment.Moment) => Promise<undefined|{ date: moment.Moment; error: string; }>
 * }>}
 */
const YearViewDatePickerInputContext = createContext();

/**
 * @param {string} source
 * @param {(toValidate: string[]) => Promise<{ date: string; error: string; }[]>} [validatingDate]
 * @param {moment.Moment} initialDate
 */
const useYearViewDatePickerInput = (source, validatingDate, initialDate) => {
  const { input: { value: startDate } } = useField('startDate');
  const { change } = useForm();
  const [selectedDate, setSelectedDate] = useState(initialDate);

  useEffect(() => {
    if (!startDate) return;
    if (startDate !== selectedDate.format('YYYY-MM-DD')) {
      setSelectedDate(moment(startDate, 'YYYY-MM-DD'));
    }
  }, [startDate, selectedDate]);

  const handleDateChange = useCallback(
    (newDate) => {
      change('startDate', convertDateToYYYYMMDD(newDate));
      setSelectedDate(newDate);
    },
    [change]
  );

  const affectValue = useCallback(
    (value) => {
      change(source, value.map((m) => convertDateToYYYYMMDD(m)));
      return value;
    },
    [change, source]
  );

  const [listDateSelected, setListDateSelected] = useState([]);

  const { input: { value: formListDateSelected } } = useField(source);

  useEffect(() => {
    if (!formListDateSelected) {
      setListDateSelected(affectValue([]));
      return;
    }
    if (!isSameDateArray(formListDateSelected, listDateSelected)) {
      const newMoments = formListDateSelected.map((d) => moment(d, 'YYYY-MM-DD'));
      setListDateSelected(affectValue(newMoments));
    }
  }, [formListDateSelected, affectValue, listDateSelected]);

  const toggleDate = useCallback(
    (date) => {
      setListDateSelected((old) => {
        const idx = old.findIndex((e) => e.isSame(date, 'day'));
        if (idx >= 0) {
          old.splice(idx, 1);
        } else {
          old.push(date);
        }
        return affectValue([...old]);
      });
    },
    [affectValue]
  );

  const toggleDayOfTheWeek = useCallback(
    (initDate, dayOfTheWeek) => {
      const fixedDate = initDate.clone().startOf('month');
      const datePlus4Month = initDate.clone().add(4, 'month');

      const toSet = [];
      while (fixedDate.isBefore(datePlus4Month, 'month')) {
        if (fixedDate.day() === dayOfTheWeek) {
          toSet.push(fixedDate.clone());
        }
        fixedDate.add(1, 'day');
      }

      setListDateSelected((old) => {
        toSet.forEach((d) => {
          const idx = old.findIndex((e) => e.isSame(d, 'day'));
          if (idx >= 0) {
            old.splice(idx, 1);
          } else {
            old.push(d);
          }
        });
        return affectValue([...old]);
      });
    },
    [affectValue]
  );

  const d1 = useMemo(() => selectedDate.clone(), [selectedDate]);
  const d2 = useMemo(() => selectedDate.clone().add(4, 'month'), [selectedDate]);
  const d3 = useMemo(() => selectedDate.clone().add(8, 'month'), [selectedDate]);

  const [, setTimeoutValidate] = useState();
  const [, setCbValidate] = useState([]);

  /**
   * @param {moment.Moment} date
   */
  const validateDate = useCallback(
    (date) => {
      return new Promise((res) => {
        setTimeoutValidate((old) => {
          clearTimeout(old);
          setCbValidate((cbValidate) => {
            return [...cbValidate, { cb: res, date }];
          });
          return setTimeout(() => {
            setCbValidate((cbValidate) => {
              (async () => {
                const allDates = Array.from(
                  new Set(cbValidate.map((e) => convertDateToYYYYMMDD(e.date)))
                );
                const outputDateInvalid = (await validatingDate?.(allDates)) || [];
                cbValidate.forEach((e) => {
                  const found = outputDateInvalid.find(
                    (d) =>
                      convertDateToYYYYMMDD(moment(d.date, 'YYYY-MM-DD')) ===
                      convertDateToYYYYMMDD(e.date)
                  );
                  e.cb(found);
                });
              })();
              return [];
            });
          }, 1000);
        });
      });
    },
    [validatingDate]
  );

  return {
    listDateSelected,
    toggleDate,
    toggleDayOfTheWeek,
    selectedDate,
    handleDateChange,
    d1,
    d2,
    d3,
    validateDate,
  };
};

/**
 * @param {{
 *  source: string;
 *  initialDate?: Date|moment.Moment;
 *  validatingDate?: (toValidate: string[]) => Promise<{ date: string; error: string }[]>;
 * }} props
 */
export const YearViewDatePickerInput = ({ source, validatingDate, initialDate = moment(), setValidating }) => {
  const { listDateSelected, toggleDate, toggleDayOfTheWeek, selectedDate, handleDateChange, d1, d2, d3, validateDate } = useYearViewDatePickerInput(source, validatingDate, moment(initialDate));
  const translate = useTranslate();

  return <YearViewDatePickerInputContext.Provider value={{ listDateSelected, toggleDate, toggleDayOfTheWeek, validateDate, setValidating }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem' }}>
      <DatePicker
        value={selectedDate}
        onChange={handleDateChange}
        views={['month', 'year']}
        openTo="month"
        label={translate('ra.input.field.starting_month')}
        margin="dense"
      />
      <div style={{ flex: 1 }}></div>
      <div className="ex-selected">
        <div>{translate('ra.message.valid')}</div>
        <div className="ex-selected-success"></div>
      </div>
      <div className="ex-selected">
        <div>{translate('ra.message.invalid')}</div>
        <div className="ex-selected-error"></div>
      </div>
      <div className="ex-selected">
        <div>{translate('ra.message.validating')}</div>
        <div className="ex-selected-loading"></div>
      </div>
    </div>

    <div className="year-view-date-picker">
      <table>
        <thead>
          <TableHeadRows date={d1} />
        </thead>
        <tbody>
          <TableBodyRows date={d1} />
        </tbody>
      </table>
      <table>
        <thead>
          <TableHeadRows date={d2} />
        </thead>
        <tbody>
          <TableBodyRows date={d2} />
        </tbody>
      </table>
      <table>
        <thead>
          <TableHeadRows date={d3} />
        </thead>
        <tbody>
          <TableBodyRows date={d3} />
        </tbody>
      </table>
    </div>
  </YearViewDatePickerInputContext.Provider>
};
